import { useState } from 'react';
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink';
import { trackService, TrackCreateRequest, uploadTrackImage, uploadLessonFiles } from 'data/services/trackService';
import { TrackDraft, Lesson } from '../types';
import { showToast } from 'components/toast';
import { useVideoUpload } from './useVideoUpload';
import { videoProviderAllowInputUrl } from '../../create/components/trackLessonsFormCard/TrackLessonsFormCard';

export const useTrackCreation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [createdTrackId, setCreatedTrackId] = useState<string | null>(null);
  const [uploadSteps, setUploadSteps] = useState<any[]>([]);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [overallProgress, setOverallProgress] = useState(0);
  const navigate = useNavigate();
  const videoUpload = useVideoUpload();

  const transformDataToApi = (draft: TrackDraft, lessons: Lesson[], status: 'Draft' | 'Publish'): TrackCreateRequest => {
    return {
      title: draft.title,
      description: draft.description || undefined,
      status: status === 'Draft' ? 1 : 2, // 1 = Draft, 2 = Publish
      lessons: lessons.map((lesson, index) => {
        const videoProviderAllowUrl = videoProviderAllowInputUrl(lesson.videoProvider??"");
        return {
          title: lesson.title,
          description: lesson.description || undefined,
          videoId: lesson.videoId || null,
          minutes: lesson.minutes || 0,
          sequence: lesson.sequence || index + 1,
          videoUrl: videoProviderAllowUrl ? lesson.videoUrl: null,
          videoProvider: lesson.videoProvider,
          permissions: {
            allowFileUploads: lesson.allowFiles || false,
            allowQuestions: lesson.allowQuestions || false
          },
      }}),
    };
  };

  const saveTrack = async (draft: TrackDraft, lessons: Lesson[]) => {
    try {
      setIsLoading(true);
      
      const apiData = transformDataToApi(draft, lessons, 'Draft');
      
      const response = await trackService.createTrack(apiData);
      
      setCreatedTrackId(response.id);
            
      return response;
    } catch (error: any) {
      console.error('Erro ao salvar trilha:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const publishTrack = async (draft: TrackDraft, lessons: Lesson[]) => {
    try {
      setIsLoading(true);
      
      const apiData = transformDataToApi(draft, lessons, 'Publish');
      
      const response = await trackService.createTrack(apiData);
      
      setCreatedTrackId(response.id);
            
      return response;
    } catch (error: any) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const uploadVideoForLesson = async (lessonId: string, file: File) => {
    try {
      const videoId = await videoUpload.uploadVideo(lessonId, file);
      return videoId;
    } catch (error: any) {
      throw error;
    }
  };

  const updateStepStatus = (stepId: string, status: 'pending' | 'in-progress' | 'completed' | 'error', message?: string) => {
    setUploadSteps(prev => prev.map(step => 
      step.id === stepId 
        ? { ...step, status, message }
        : step
    ));
  };

  const calculateProgress = (completedSteps: number, totalSteps: number) => {
    return Math.round((completedSteps / totalSteps) * 100);
  };

  const handleCompleteFlow = async (
    draft: TrackDraft, 
    lessons: Lesson[], 
    status: 'Draft' | 'Publish', 
    videoFiles?: { [lessonIndex: number]: File },
    attachmentFiles?: { [lessonIndex: number]: File[] }
  ) => {
    try {
      const steps = [
        { id: 'track', name: 'Criando trilha e aulas', status: 'pending' as const },
        { id: 'videos', name: 'Upload dos vídeos', status: 'pending' as const },
      ];
      
      setUploadSteps(steps);
      setOverallProgress(0);
      setIsLoading(true);

      setCurrentStep('track');
      updateStepStatus('track', 'in-progress', 'Criando trilha...');
      
      const trackData = status === 'Draft' 
        ? await saveTrack(draft, lessons)
        : await publishTrack(draft, lessons);

        updateStepStatus('track', 'completed', 'Trilha criada com sucesso');
        setOverallProgress(calculateProgress(1, steps.length));

      if (draft.imageFile && trackData.id) {
        setCurrentStep('image');
        updateStepStatus('image', 'in-progress', 'Enviando imagem...');
        
        try {
          const imageUrl = await uploadTrackImage(trackData.id, draft.imageFile);
          
          updateStepStatus('image', 'completed', 'Imagem enviada');
          setOverallProgress(calculateProgress(2, steps.length));
          
          if (draft.imageUrl && draft.imageUrl.startsWith('blob:')) {
            URL.revokeObjectURL(draft.imageUrl);
          }
        } catch (imageError) {
          updateStepStatus('image', 'error', 'Erro no upload da imagem');
        }
      } else {
        updateStepStatus('image', 'completed', 'Nenhuma imagem para enviar');
        setOverallProgress(calculateProgress(2, steps.length));
      }

      if (videoFiles && trackData.lessons && trackData.lessons.length > 0) {
        setCurrentStep('videos');
        updateStepStatus('videos', 'in-progress', 'Enviando vídeos...');
        
        let videoCount = 0;
        const totalVideos = Object.keys(videoFiles).length;
        
        for (const [lessonIndex, file] of Object.entries(videoFiles)) {
          const originalIdx = parseInt(lessonIndex);
          const original = lessons[originalIdx];
          const mapped = trackData.lessons.find(l => {
            const sameSeq = (l.sequence || originalIdx + 1) === (original.sequence || originalIdx + 1);
            return sameSeq;
          }) || trackData.lessons[originalIdx];
          const lesson = mapped;
          if (lesson && file) {
            try {
              updateStepStatus('videos', 'in-progress', `Enviando vídeo ${videoCount + 1}/${totalVideos}`);
              
              await uploadVideoForLesson(lesson.id, file);
              videoCount++;
            } catch (error) {
            }
          }
        }
        
        updateStepStatus('videos', 'completed', `${videoCount} vídeo(s) enviado(s)`);
        setOverallProgress(calculateProgress(3, steps.length));
      } else {
        updateStepStatus('videos', 'completed', 'Nenhum vídeo para enviar');
        setOverallProgress(calculateProgress(3, steps.length));
      }

      if (attachmentFiles && trackData.lessons && trackData.lessons.length > 0) {
        setCurrentStep('files');
        updateStepStatus('files', 'in-progress', 'Enviando arquivos...');
        
        let fileCount = 0;
        const totalFiles = Object.values(attachmentFiles).reduce((acc, files) => acc + files.length, 0);
        
        for (const [lessonIndex, files] of Object.entries(attachmentFiles)) {
          const originalIdx = parseInt(lessonIndex);
          const original = lessons[originalIdx];
          const mapped = trackData.lessons.find(l => {
            const sameSeq = (l.sequence || originalIdx + 1) === (original.sequence || originalIdx + 1);
            return sameSeq;
          }) || trackData.lessons[originalIdx];
          const lesson = mapped;
          if (lesson && files && files.length > 0) {
            try {
              updateStepStatus('files', 'in-progress', `Enviando arquivos ${fileCount + files.length}/${totalFiles}`);
              
              await uploadLessonFiles(trackData.id, lesson.id, files);
              fileCount += files.length;
            } catch (error) {
            }
          }
        }
        
        updateStepStatus('files', 'completed', `${fileCount} arquivo(s) enviado(s)`);
        setOverallProgress(100);
      } else {
        updateStepStatus('files', 'completed', 'Nenhum arquivo para enviar');
        setOverallProgress(100);
      }

      setCurrentStep(null);
      showToast('success', 'Trilha criada com sucesso!');
      
      setTimeout(() => {
        navigate('/hub/tracks');
      }, 1000);
      
    } catch (error) {
      console.error('Erro no fluxo completo:', error);
      setCurrentStep(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    saveTrack,
    publishTrack,
    uploadVideoForLesson,
    handleCompleteFlow,
    isLoading: isLoading || videoUpload.isUploading,
    uploadProgress: overallProgress || videoUpload.uploadProgress,
    uploadError: videoUpload.error,
    createdTrackId,
    uploadSteps,
    currentStep,
    isUploading: videoUpload.isUploading,
  };
};
